/**
 * Pure model for the past seasons web home (ISSUE-754): every copy string
 * and pure rule the `Past seasons` row and its dialog need. No sum, no sort
 * by points and no rank computation lives here (R2, D3 of the plan) -- the
 * list's order and every leaderboard number are server fields, read as is.
 */
import type { useSeasonLeaderboardGet } from "@sportsmet/contest-sdk/private";

import type { typeSeasonListItem } from "./seasonCardModel";

export type typeSeasonLeaderboard = NonNullable<
  NonNullable<ReturnType<typeof useSeasonLeaderboardGet>["data"]>["data"]
>;
export type typeSeasonLeaderboardMember =
  typeSeasonLeaderboard["members"][number];

export type typePastSeasonRow = {
  seasonId: string;
  title: string;
  meta: string;
};

export type typePastSeasonSummaryPhase =
  "loading" | "failed" | "empty" | "ready";

export const PAST_SEASONS_DIALOG_NAME = "past-seasons";
export const PAST_SEASONS_ROW_LABEL = "Past seasons";
export const PAST_SEASONS_DIALOG_TITLE = "Past seasons";
export const PAST_SEASONS_BACK_LABEL = "All past seasons";
export const PAST_SEASON_EMPTY_MESSAGE =
  "Nobody scored points or won an award this season.";
export const PAST_SEASON_FAILED_MESSAGE = "We couldn't load this season.";
export const PAST_SEASON_RETRY_LABEL = "Try again";

export function pastSeasonsCountLabel(count: number): string {
  return count === 1 ? "1 past season" : `${String(count)} past seasons`;
}

export function pointsLabel(points: number): string {
  return points === 1 ? "1 point" : `${String(points)} points`;
}

export function listPastSeasons(
  items: readonly typeSeasonListItem[],
): typeSeasonListItem[] {
  return items.filter((item) => !item.isCurrent);
}

export function seasonDateRangeLabel(input: {
  startedAt: string;
  endedAt: string | null | undefined;
  timeZone: string;
}): string {
  const formatter = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    timeZone: input.timeZone,
  });
  const start = formatter.format(new Date(input.startedAt));
  if (input.endedAt === null || input.endedAt === undefined) {
    return `Since ${start}`;
  }
  const end = formatter.format(new Date(input.endedAt));
  return `${start} to ${end}`;
}

export function buildPastSeasonRows(
  items: readonly typeSeasonListItem[],
  timeZone: string,
): typePastSeasonRow[] {
  return listPastSeasons(items).map((item) => ({
    seasonId: item.seasonId,
    title: item.seasonTitle,
    meta: `${String(item.seasonYear)} · ${seasonDateRangeLabel({
      startedAt: item.startedAt,
      endedAt: item.endedAt,
      timeZone,
    })}`,
  }));
}

export function openPastSeasonSearch<TSearch extends Record<string, unknown>>(
  search: TSearch,
  seasonId: string,
): TSearch & { seasonId: string } {
  return { ...search, seasonId };
}

export function closePastSeasonSearch<TSearch extends Record<string, unknown>>(
  search: TSearch,
): TSearch {
  return Object.fromEntries(
    Object.entries(search).filter(([key]) => key !== "seasonId"),
  ) as TSearch;
}

export function resolvePastSeasonSummaryPhase(input: {
  isLoading: boolean;
  isError: boolean;
  errorCode: string | null;
  memberCount: number;
}): typePastSeasonSummaryPhase {
  if (input.isLoading) {
    return "loading";
  }
  if (input.isError || input.errorCode !== null) {
    return "failed";
  }
  if (input.memberCount === 0) {
    return "empty";
  }
  return "ready";
}
